/**
 * The Updates page: release notes, newest first.
 *
 * To publish a release, add an entry at the top of `releases` (here and in
 * `../ar/updates.js`) with its `version`, `date`, `title` and `summary`, and
 * any of `new`, `improved` and `fixed`: lists of `{ title, text }` changes.
 * The first release is badged Latest; the sidebar, filters and counts follow
 * on their own. With no releases, the page shows its "no release notes yet"
 * state.
 */
export const UPDATES = {
  eyebrow: 'Release notes',
  title: "What's new at MeetNPlan",
  text: 'Every release of MeetNPlan, with what is new, what got better and what we fixed.',
  latest: 'Latest',
  soon: 'First release coming soon',
  releasesLabel: 'Releases',
  showLabel: 'Show',
  kinds: { all: 'All', new: 'New', improved: 'Improved', fixed: 'Fixed' },
  none: 'Nothing of this type yet.',
  empty: {
    title: 'No release notes yet',
    text: "When we ship something new, improve a feature or fix a bug, you'll find it here.",
  },
  report: {
    title: 'Spotted a bug or something not working?',
    text: "Tell us what happened and we'll look into it. A screenshot helps.",
    cta: { label: 'Report an issue', href: '/contact' },
  },
  releases: [
    {
      version: 'v2.4',
      date: '20 Oct 2026',
      title: 'Messaging and guest invites',
      summary:
        'Your team can now chat inside MeetNPlan, and you can invite people from outside the company to any meeting.',
      new: [
        {
          title: 'Internal messaging',
          text: 'Direct, group and meeting chats with read receipts, replies and file sharing.',
        },
        {
          title: 'Client invites',
          text: 'Invite people from outside the company. They are marked as external guests.',
        },
      ],
      fixed: [
        {
          title: 'Unread count now clears',
          text: 'The unread badge stayed on after reading a chat. It now clears right away.',
        },
      ],
    },
    {
      version: 'v2.3',
      date: '2 Oct 2026',
      title: 'Faster invites by email',
      summary: 'Sending and answering invites now takes one click.',
      new: [
        {
          title: 'One-click email invites',
          text: 'Send meeting invites by email in one click, with replies tracked for every attendee.',
        },
      ],
      improved: [
        {
          title: 'Faster calendar',
          text: 'The week view loads about twice as fast on large teams.',
        },
      ],
    },
    {
      version: 'v2.2',
      date: '18 Sep 2026',
      title: 'Calendar sync',
      summary: 'Meetings now appear in the calendars your team already uses.',
      new: [
        {
          title: 'Google Calendar',
          text: 'Meetings sync to Google Calendar, and invites go out by email.',
        },
      ],
      fixed: [
        {
          title: 'Time zone on invites',
          text: 'Invites sent across time zones now show the right local time.',
        },
      ],
    },
    {
      version: 'v2.1',
      date: '4 Sep 2026',
      title: 'Better rooms',
      summary: 'Room setup is clearer and room status is live.',
      improved: [
        {
          title: 'Room management',
          text: 'Set up rooms with capacity and equipment, and see which are free right now.',
        },
      ],
      fixed: [
        {
          title: 'Room photos',
          text: 'Room photos no longer appear stretched on phones.',
        },
      ],
    },
    {
      version: 'v2.0',
      date: '20 Aug 2026',
      title: 'MeetNPlan 2.0',
      summary: 'A new look across all four panels, with full Arabic support.',
      new: [
        {
          title: 'New design',
          text: 'A cleaner layout across the User, Reception, HR and Admin panels.',
        },
        {
          title: 'Arabic first',
          text: 'Full right-to-left layout and Arabic text, with English one tap away.',
        },
      ],
    },
  ],
}
